import { useTranslation } from "react-i18next";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "@/hooks/useReduxHooks";
import { useAdvisor } from "@/components/ai-assistant/advisor";
import { AI_PROMPT_MAX_LENGTH } from "@/components/ai-assistant/context";
import {
  detectFencedForm,
  looksLikeFormSpec,
  type MayaFormSpec,
} from "@/components/ai-assistant/maya-form";
import { useLocale } from "@/hooks/useLocale";
import type {
  IAiGeneratedAttachment,
  IChatAttachment,
} from "@/types/(my-space)/ai-assistant.interface";
import { isHttpUrl } from "@/utils/file.utils";
import { useStreamingChat } from "@/hooks/useStreamingChat";
import { useReadAloud } from "@/hooks/useReadAloud";
import { AiChatType } from "@/utils/enums/ai-assistant.enum";
import { selectUser } from "@/store/slices/user.slice";

import { INITIAL_GREETING_KEY, STORAGE_KEYS } from "../constants";
import { useMayaPageContext } from "../hooks/use-page-context";
import { MAYA_SPACES } from "../spaces";
import { useMayaSidebar } from "./use-maya-sidebar";
import { useMayaSpace } from "./use-maya-space";
import type {
  IMayaError,
  IMayaMessage,
  IMayaStep,
  MayaMessageRole,
} from "../types";
import {
  HISTORY_PAGE_SIZE,
  historyToMessages,
  prependMessages,
} from "../utils/history";
import {
  safeJsonParse,
  sessionGet,
  sessionRemove,
  sessionSet,
} from "../utils/safe-storage";

import { ChatContext, type ChatContextValue } from "./chat-context";

/** Where paging through a saved chat's history has got to. */
interface HistoryCursor {
  /** Last history page loaded (1 = the newest turns). */
  page: number;
  /** Older turns exist that aren't loaded yet. */
  hasEarlier: boolean;
}

interface ChatState {
  messages: IMayaMessage[];
  sessionId: string | null;
  error: IMayaError | null;
  cursor: HistoryCursor;
  /** A temporary chat: not saved to history (kept server-side for a few
   *  hours only), like the full page's temporary mode. */
  temporary: boolean;
}

const NO_HISTORY: HistoryCursor = { page: 1, hasEarlier: false };

type ChatAction =
  | {
      type: "ADD_MESSAGE";
      role: MayaMessageRole;
      text: string;
      id?: string;
      files?: Pick<
        IMayaMessage,
        | "attachments"
        | "generatedAttachments"
        | "attachmentErrors"
        | "steps"
        | "i18nKey"
      >;
    }
  | {
      type: "UPDATE_MESSAGE_STATUS";
      id: string;
      status: IMayaMessage["status"];
    }
  | { type: "SET_SESSION"; sessionId: string | null }
  | { type: "PATCH_MESSAGE"; id: string; patch: Partial<IMayaMessage> }
  | { type: "INSERT_AFTER"; afterId: string; message: IMayaMessage }
  /** Drop everything after a message and append one in its place. */
  | { type: "TRUNCATE_AFTER"; afterId: string; message: IMayaMessage }
  | { type: "REMOVE_MESSAGE"; id: string }
  | { type: "SET_ERROR"; error: IMayaError | null }
  | {
      type: "HYDRATE";
      messages: IMayaMessage[];
      sessionId: string | null;
      cursor: HistoryCursor;
      temporary?: boolean;
    }
  | {
      type: "LOAD_CONVERSATION";
      messages: IMayaMessage[];
      sessionId: string;
      /** The oldest history page loaded (1 unless opened at a prompt). */
      page?: number;
      hasEarlier: boolean;
    }
  | { type: "PREPEND_HISTORY"; messages: IMayaMessage[]; cursor: HistoryCursor }
  | { type: "RESET"; temporary?: boolean };

const createId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const buildMessage = (
  role: MayaMessageRole,
  text: string,
  id: string = createId(),
): IMayaMessage => ({
  id,
  role,
  text,
  ts: Date.now(),
  status: "sent",
});

const greeting = (): IMayaMessage[] => [
  { ...buildMessage("bot", INITIAL_GREETING_KEY), i18nKey: true },
];

const buildInitialState = (temporary = false): ChatState => ({
  messages: greeting(),
  sessionId: null,
  error: null,
  cursor: NO_HISTORY,
  temporary,
});

const reducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case "HYDRATE":
      return {
        ...state,
        messages: action.messages.length > 0 ? action.messages : greeting(),
        sessionId: action.sessionId,
        cursor: action.cursor,
        temporary: action.temporary ?? false,
      };
    case "LOAD_CONVERSATION":
      return {
        // The greeting opens new chats only; it would sit oddly at the top
        // of an old one (and under "Load earlier messages"). It is kept just
        // for a saved chat with nothing in it.
        messages: action.messages.length > 0 ? action.messages : greeting(),
        sessionId: action.sessionId,
        error: null,
        cursor: { page: action.page ?? 1, hasEarlier: action.hasEarlier },
        // Saved chats only — the list never holds temporary ones.
        temporary: false,
      };
    case "PREPEND_HISTORY":
      return {
        ...state,
        messages: prependMessages(state.messages, action.messages),
        cursor: action.cursor,
      };
    case "ADD_MESSAGE":
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            ...buildMessage(action.role, action.text, action.id),
            ...action.files,
          },
        ],
      };
    case "UPDATE_MESSAGE_STATUS":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, status: action.status } : m,
        ),
      };
    case "SET_SESSION":
      return {
        ...state,
        sessionId: action.sessionId,
        // History paging belongs to a session; a different (or no) session —
        // e.g. the stale-session retry — starts with nothing older to load.
        cursor:
          action.sessionId === state.sessionId ? state.cursor : NO_HISTORY,
      };
    case "PATCH_MESSAGE":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.id ? { ...m, ...action.patch } : m,
        ),
      };
    case "INSERT_AFTER": {
      const at = state.messages.findIndex((m) => m.id === action.afterId);
      if (at < 0) return state;
      const messages = [...state.messages];
      messages.splice(at + 1, 0, action.message);
      return { ...state, messages };
    }
    case "TRUNCATE_AFTER": {
      const at = state.messages.findIndex((m) => m.id === action.afterId);
      if (at < 0) return state;
      return {
        ...state,
        messages: [...state.messages.slice(0, at + 1), action.message],
      };
    }
    case "REMOVE_MESSAGE":
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.id),
      };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "RESET":
      return buildInitialState(action.temporary);
    default:
      return state;
  }
};

/** A stored message id: numeric for saved chats, a hex string for
 *  temporary ones (kept in Redis). */
const isStoredId = (v: unknown): v is number | string =>
  typeof v === "number" || (typeof v === "string" && v.length > 0);

/** A reply can ask for structured input: the SSE `form` field wins, else a
 *  fenced ```maya-form block in the answer (same gate as the full page). */
const pickFormSpec = (answer: string, form: unknown): MayaFormSpec | null =>
  looksLikeFormSpec(form) ? form : detectFencedForm(answer).spec;

/** The form a reopened chat's last reply still asks for. Only the reply text
 *  is saved, so without this the reply says "fill in the form below" over
 *  nothing. */
const formOfLastReply = (messages: IMayaMessage[]): MayaFormSpec | null => {
  const last = messages[messages.length - 1];
  return last?.role === "bot" && !last.i18nKey
    ? detectFencedForm(last.text).spec
    : null;
};

/** A reply's files as a message patch — clears the old ones when there are
 *  none, and keeps only http(s) links (see isHttpUrl). */
const filesPatch = (
  attachments?: IAiGeneratedAttachment[] | null,
  errors?: string[] | null,
): Partial<IMayaMessage> => {
  const files = (attachments ?? []).filter((a) => isHttpUrl(a.url));
  return {
    generatedAttachments: files.length ? files : undefined,
    attachmentErrors: errors?.length ? errors : undefined,
  };
};

const isCursor = (v: unknown): v is HistoryCursor =>
  !!v &&
  typeof v === "object" &&
  typeof (v as HistoryCursor).page === "number" &&
  typeof (v as HistoryCursor).hasEarlier === "boolean";

/** Every space's saved chat and draft — on sign-out, or another user. */
const clearEveryPersistedChat = () => {
  Object.values(MAYA_SPACES).forEach(({ storage }) => {
    sessionRemove(storage.chat);
    sessionRemove(storage.draft);
  });
  sessionRemove(STORAGE_KEYS.legacyMessages);
  sessionRemove(STORAGE_KEYS.legacySessionId);
  sessionRemove(STORAGE_KEYS.scopedUser);
};

interface PersistedChat {
  messages: IMayaMessage[];
  sessionId: string | null;
  cursor: HistoryCursor;
  temporary?: boolean;
}

/** A space's persisted chat — or, from before it was one record, the old
 *  keys (`withLegacy`: My Space, the only space back then). */
const readPersistedChat = (
  key: string,
  withLegacy: boolean,
): PersistedChat | null => {
  const record = safeJsonParse<Partial<PersistedChat> | null>(
    sessionGet(key),
    null,
  );
  if (record && Array.isArray(record.messages)) {
    return {
      messages: record.messages,
      sessionId: typeof record.sessionId === "string" ? record.sessionId : null,
      cursor: isCursor(record.cursor) ? record.cursor : NO_HISTORY,
      temporary: record.temporary === true,
    };
  }
  if (!withLegacy) return null;
  const messages = safeJsonParse<IMayaMessage[]>(
    sessionGet(STORAGE_KEYS.legacyMessages),
    [],
  );
  const sessionId = sessionGet(STORAGE_KEYS.legacySessionId);
  sessionRemove(STORAGE_KEYS.legacyMessages);
  sessionRemove(STORAGE_KEYS.legacySessionId);
  return messages.length > 0 || sessionId
    ? { messages, sessionId, cursor: NO_HISTORY }
    : null;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const { t } = useTranslation(["common"]);
  const space = useMayaSpace();
  const [state, dispatch] = useReducer(reducer, undefined, buildInitialState);
  const {
    sendMessage: streamSend,
    retryMessage: streamRetry,
    isStreaming: isTyping,
    cancel,
    abort,
  } = useStreamingChat(space.endpoints.chat);
  const lastUserTextRef = useRef<string | null>(null);
  // Each load gets a number; a response that comes back after a newer load
  // (or a reset, or logout) has started is dropped instead of applied.
  const loadSeqRef = useRef(0);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  /** Supersede any in-flight load and drop its loading state. */
  const cancelLoads = useCallback(() => {
    loadSeqRef.current += 1;
    setLoadingConversation(false);
    setLoadingEarlier(false);
  }, []);
  // Read by `send`, which is defined before the loading state it guards on.
  const loadingConversationRef = useRef(false);
  // The reply being regenerated, or awaiting an edited prompt's new answer —
  // it shows "thinking" in place until the new text arrives.
  const [pendingReplyId, setPendingReplyId] = useState<string | null>(null);
  const pendingReplyIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    pendingReplyIdRef.current = pendingReplyId;
  }, [pendingReplyId]);
  useLayoutEffect(() => {
    loadingConversationRef.current = loadingConversation;
  }, [loadingConversation]);
  // Each edit gets a number too; leaving the chat (new chat, another chat,
  // logout) bumps it, so an edited prompt's answer that arrives afterwards
  // is dropped instead of landing in whatever is on screen.
  const editSeqRef = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const lastUserAttachmentsRef = useRef<IChatAttachment[]>([]);
  // The current turn's answer has arrived (the stream stays open a moment
  // longer for its stored id) — stopping now must not mark it as cut off.
  const finalArrivedRef = useRef(false);
  const { formatDate } = useLocale();
  // A form the latest reply asked the user to fill in (shown above the
  // composer; its answers are merged into the draft, never auto-sent).
  const [activeForm, setActiveForm] = useState<MayaFormSpec | null>(null);
  const hydratedRef = useRef(false);
  // Set when a stored chat is put back on screen: a reply may have finished
  // on the server while it was away (in the other space, or another tab).
  const refreshOnOpenRef = useRef(false);

  // "Read responses aloud" preference (shared with the full-page assistant).
  const { readAloud, toggleReadAloud, speakIfEnabled, stopSpeaking } =
    useReadAloud();

  // The page open beside the sidebar rides along with each turn unless the
  // user switched it off. Read through a ref so a route change doesn't
  // rebuild `send` (and every consumer of it).
  const pageContext = useMayaPageContext();
  const { sharePageContext, open, staleChatRef } = useMayaSidebar();
  const pageContextRef = useRef(pageContext);
  const sharePageContextRef = useRef(sharePageContext);
  useLayoutEffect(() => {
    pageContextRef.current = pageContext;
    sharePageContextRef.current = sharePageContext;
  }, [pageContext, sharePageContext]);

  // This provider outlives the sidebar (it stays mounted while closed), so it
  // only unmounts with the app layout — drop any stream still running then.
  useEffect(() => abort, [abort]);

  // The step the AI is on right now ("Looking up payroll runs…"), streamed
  // before the answer. Cleared when the turn ends.
  const [status, setStatus] = useState<string | null>(null);

  const { user } = useAppSelector(selectUser);
  const userId = user?.id ? String(user.id) : null;
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (hydratedRef.current || !userId) return;
    const persistedUserId = sessionGet(STORAGE_KEYS.scopedUser);
    if (persistedUserId && persistedUserId !== userId) {
      clearEveryPersistedChat();
    } else {
      const persisted = readPersistedChat(
        space.storage.chat,
        space.space === "my-space",
      );
      if (persisted) {
        dispatch({ type: "HYDRATE", ...persisted });
        setActiveForm(formOfLastReply(persisted.messages));
        // It may have moved on while it was put away (see below).
        refreshOnOpenRef.current = true;
      }
    }
    sessionSet(STORAGE_KEYS.scopedUser, userId);
    prevUserIdRef.current = userId;
    hydratedRef.current = true;
  }, [userId, space]);

  useEffect(() => {
    const prev = prevUserIdRef.current;
    if (prev && !userId) {
      cancelLoads();
      editSeqRef.current += 1;
      setPendingReplyId(null);
      // Every space's chat and unsent draft are the signed-out user's.
      clearEveryPersistedChat();
      lastUserTextRef.current = null;
      lastUserIdRef.current = null;
      hydratedRef.current = false;
      dispatch({ type: "RESET" });
    }
    prevUserIdRef.current = userId;
  }, [userId, cancelLoads]);

  // One record, written whole. If it doesn't fit (a very long chat), store
  // nothing rather than a stale copy — a reload then starts clean instead of
  // showing one chat under another's session.
  useEffect(() => {
    if (!hydratedRef.current || !userId) return;
    const record: PersistedChat = {
      messages: state.messages,
      sessionId: state.sessionId,
      cursor: state.cursor,
      temporary: state.temporary,
    };
    if (!sessionSet(space.storage.chat, JSON.stringify(record))) {
      sessionRemove(space.storage.chat);
    }
  }, [
    state.messages,
    state.sessionId,
    state.cursor,
    state.temporary,
    userId,
    space,
  ]);

  // A finished turn changes the chat list (a new chat appears, a title is
  // generated, the order moves) — refresh it if it's showing.
  const appDispatch = useAppDispatch();
  const refreshChatList = useCallback(() => {
    appDispatch(space.invalidateChats());
  }, [appDispatch, space]);

  const send = useCallback(
    async (
      text: string,
      {
        attachments = [],
        replayUserId,
        forceNewSession = false,
      }: {
        /** Files already uploaded for this turn. */
        attachments?: IChatAttachment[];
        /** Re-send a failed turn in place. */
        replayUserId?: string;
        /** Start a new conversation (the stale-session retry). */
        forceNewSession?: boolean;
      } = {},
    ) => {
      const trimmed = text.trim();
      // A turn needs text or at least one file. While a saved chat is opening,
      // its history would replace this turn; while an edited prompt's reply
      // is pending, the order would tangle.
      if (
        (!trimmed && attachments.length === 0) ||
        isTyping ||
        loadingConversationRef.current ||
        pendingReplyIdRef.current
      )
        return;
      // Same cap as the API (AiChatDto) — fail here, not with a 400.
      if (trimmed.length > AI_PROMPT_MAX_LENGTH) {
        toast.error(
          t("my-space:toasts.message_is_too_long_max_characters", {
            count: AI_PROMPT_MAX_LENGTH,
          }),
          { id: "maya-error" },
        );
        return;
      }

      const userMsgId = replayUserId ?? createId();
      lastUserTextRef.current = trimmed;
      lastUserIdRef.current = userMsgId;
      lastUserAttachmentsRef.current = attachments;
      finalArrivedRef.current = false;
      setActiveForm(null);

      if (!replayUserId) {
        dispatch({
          type: "ADD_MESSAGE",
          role: "user",
          text: trimmed,
          id: userMsgId,
          files: attachments.length ? { attachments } : undefined,
        });
      } else {
        dispatch({
          type: "UPDATE_MESSAGE_STATUS",
          id: userMsgId,
          status: "pending",
        });
      }
      dispatch({ type: "SET_ERROR", error: null });
      setStatus(null);

      // Known up front so the stored id (meta-final) can be attached to it.
      const botMsgId = createId();
      // The AI's progress lines, kept on the reply ("Steps" popover).
      const steps: IMayaStep[] = [];

      const activeSessionId = forceNewSession
        ? undefined
        : (state.sessionId ?? undefined);

      await streamSend(
        {
          message: trimmed,
          sessionId: activeSessionId,
          // Saved chats persist so they load in the full AI Assistant page;
          // a temporary chat (header menu) isn't saved to history.
          isTemporary: state.temporary,
          chatType: AiChatType.TEXT,
          ...(attachments.length
            ? { attachmentUrls: attachments.map((a) => a.url) }
            : {}),
          ...(sharePageContextRef.current
            ? { pageContext: pageContextRef.current.payload }
            : {}),
        },
        {
          onMeta: ({ sessionId, lastUserMessageId }) => {
            dispatch({ type: "SET_SESSION", sessionId });
            if (isStoredId(lastUserMessageId)) {
              dispatch({
                type: "PATCH_MESSAGE",
                id: userMsgId,
                patch: { serverId: lastUserMessageId },
              });
            }
          },
          onStatus: (message) => {
            setStatus(message || null);
            if (message) {
              steps.push({ message, at: formatDate(new Date(), "hh:mm a") });
            }
          },
          onFinal: ({
            answer,
            form,
            attachments: generated,
            attachmentErrors,
          }) => {
            finalArrivedRef.current = true;
            setStatus(null);
            refreshChatList();
            setActiveForm(pickFormSpec(answer, form));
            // A reply may be only a file ("here's the CSV") — still show it.
            if (answer || generated.length || attachmentErrors.length) {
              dispatch({
                type: "ADD_MESSAGE",
                role: "bot",
                id: botMsgId,
                text: answer,
                files: {
                  ...filesPatch(generated, attachmentErrors),
                  ...(steps.length ? { steps: [...steps] } : {}),
                },
              });
              if (answer) speakIfEnabled(answer);
            }
            dispatch({
              type: "UPDATE_MESSAGE_STATUS",
              id: userMsgId,
              status: "sent",
            });
          },
          onMetaFinal: ({ lastAiMessageId }) => {
            if (isStoredId(lastAiMessageId)) {
              dispatch({
                type: "PATCH_MESSAGE",
                id: botMsgId,
                patch: { serverId: lastAiMessageId },
              });
            }
          },
          onDone: () => setStatus(null),
          onError: (err) => {
            setStatus(null);
            const message =
              err?.message ?? t("common:chrome.maya_couldnt_respond_try_again");
            // The persisted session is gone server-side (e.g. an older
            // temporary chat). Drop it and retry once as a fresh conversation.
            const sessionDead =
              !forceNewSession &&
              !!activeSessionId &&
              /conversation not found/i.test(message);
            if (sessionDead) {
              dispatch({ type: "SET_SESSION", sessionId: null });
              // Defer so the stream's isStreaming flag resets before the retry.
              setTimeout(() => {
                void sendRef.current?.(trimmed, {
                  attachments,
                  replayUserId: userMsgId,
                  forceNewSession: true,
                });
              }, 0);
              return;
            }
            dispatch({
              type: "UPDATE_MESSAGE_STATUS",
              id: userMsgId,
              status: "failed",
            });
            dispatch({
              type: "SET_ERROR",
              error: { message, failedUserMessageId: userMsgId },
            });
            toast.error(message, { id: "maya-error" });
          },
        },
      );
    },
    [
      streamSend,
      isTyping,
      state.sessionId,
      state.temporary,
      speakIfEnabled,
      refreshChatList,
      formatDate,
      t,
    ],
  );

  // Latest `send` for the deferred stale-session retry (avoids a stale closure).
  const sendRef = useRef(send);
  useLayoutEffect(() => {
    sendRef.current = send;
  }, [send]);

  const sendMessage = useCallback(
    (text: string, attachments?: IChatAttachment[]) =>
      send(text, { attachments }),
    [send],
  );

  const retryLast = useCallback(async () => {
    const text = lastUserTextRef.current;
    const id = lastUserIdRef.current;
    const attachments = lastUserAttachmentsRef.current;
    if ((!text && attachments.length === 0) || !id) return;
    await send(text ?? "", { attachments, replayUserId: id });
  }, [send]);

  // Stop: cancel server-side and locally. A fresh reply that was cut off
  // leaves a "Response stopped" note (as on the full page); a regenerate
  // keeps the previous answer, and an answer that already arrived is whole,
  // so neither needs one.
  const stop = useCallback(() => {
    const wasReplying =
      isTyping && !pendingReplyIdRef.current && !finalArrivedRef.current;
    cancel();
    setStatus(null);
    const askedId = lastUserIdRef.current;
    if (wasReplying && askedId) {
      dispatch({
        type: "INSERT_AFTER",
        afterId: askedId,
        message: {
          ...buildMessage("bot", "ai-assistant:errors.response_stopped"),
          i18nKey: true,
        },
      });
    }
  }, [cancel, isTyping]);

  const closeForm = useCallback(() => setActiveForm(null), []);

  // The advisor (what Maya draws on — company data only, or the web too), as
  // on the full page: remembered per chat, else the last one chosen.
  const { advisor, selectAdvisor } = useAdvisor({
    scope: space.advisorScope,
    sessionId: state.sessionId ?? undefined,
  });

  // Latest values for the message actions below, which stay stable so the
  // memoised bubbles they're handed to don't redraw on every update.
  const latestRef = useRef({
    messages: state.messages,
    sessionId: state.sessionId,
    isTyping,
    temporary: state.temporary,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      messages: state.messages,
      sessionId: state.sessionId,
      isTyping,
      temporary: state.temporary,
    };
  }, [state.messages, state.sessionId, isTyping, state.temporary]);

  // Saved chats (the same ones the full AI Assistant page lists) are opened
  // here newest turns first; older ones load on request.
  const [getConversation] = space.useGetConversation();

  /** Replace the open chat with its newest turns as the server holds them —
   *  quietly (no skeleton), and only if `stillValid()` says nothing on screen
   *  moved on while the request was out. Resolves to the turns it showed. */
  const reloadNewest = useCallback(
    async (
      sessionId: string,
      stillValid: () => boolean,
    ): Promise<IMayaMessage[] | null> => {
      try {
        const res = await getConversation({
          sessionId,
          page: 1,
          limit: HISTORY_PAGE_SIZE,
        }).unwrap();
        if (!stillValid()) return null;
        const messages = historyToMessages(res.data.history);
        dispatch({
          type: "LOAD_CONVERSATION",
          sessionId,
          messages,
          hasEarlier: res.meta.page < res.meta.lastPage,
        });
        return messages;
      } catch {
        // Not worth an error toast: what's on screen stays as it was.
        return null;
      }
    },
    [getConversation],
  );

  // Edit a sent prompt: the server re-answers it (not streamed), and — as on
  // the full page — drops every turn that came after it.
  const [editPrompt] = space.useEditMessage();
  const editMessage = useCallback(
    async (id: string, text: string): Promise<boolean> => {
      const {
        messages,
        sessionId,
        isTyping: busy,
        temporary,
      } = latestRef.current;
      const at = messages.findIndex((m) => m.id === id);
      const prompt = messages[at];
      const content = text.trim();
      // The edit endpoint works on saved messages (numeric ids) only.
      if (
        temporary ||
        !sessionId ||
        typeof prompt?.serverId !== "number" ||
        prompt.role !== "user" ||
        !content
      )
        return false;
      if (busy || pendingReplyIdRef.current) {
        toast.error(t("ai-assistant:toasts.wait_for_ai_to_respond"), {
          id: "maya-busy",
        });
        return false;
      }
      const previousText = prompt.text;
      const next = messages[at + 1];
      // The answer being replaced. A "Response stopped" note isn't one: the
      // new answer goes in after the prompt instead.
      const reply = next?.role === "bot" && !next.i18nKey ? next : null;
      const replyId = reply?.id ?? createId();
      const seq = ++editSeqRef.current;

      stopSpeaking();
      setActiveForm(null);
      dispatch({ type: "PATCH_MESSAGE", id, patch: { text: content } });
      if (!reply) {
        dispatch({
          type: "INSERT_AFTER",
          afterId: id,
          message: buildMessage("bot", "", replyId),
        });
      }
      setPendingReplyId(replyId);
      try {
        const res = await editPrompt({
          messageId: prompt.serverId,
          content,
        }).unwrap();
        if (seq !== editSeqRef.current) return false;
        const data = res.data;
        const answer = data.answer ?? "";
        dispatch({
          type: "TRUNCATE_AFTER",
          afterId: id,
          message: {
            ...buildMessage("bot", answer, replyId),
            ...filesPatch(data.generatedAttachments, data.attachmentErrors),
            ...(data.answerId ? { serverId: data.answerId } : {}),
          },
        });
        setActiveForm(pickFormSpec(answer, undefined));
        if (answer) speakIfEnabled(answer);
        refreshChatList();
        return true;
      } catch {
        if (seq !== editSeqRef.current) return false;
        dispatch({ type: "PATCH_MESSAGE", id, patch: { text: previousText } });
        if (!reply) dispatch({ type: "REMOVE_MESSAGE", id: replyId });
        toast.error(t("my-space:toasts.failed_to_edit_message"), {
          id: "maya-error",
        });
        // The server saves the edit and drops the later turns before asking
        // the AI, so a failure there leaves the chat changed: show it as the
        // server now has it.
        void reloadNewest(sessionId, () => {
          const latest = latestRef.current;
          return (
            seq === editSeqRef.current &&
            latest.sessionId === sessionId &&
            !latest.isTyping &&
            !pendingReplyIdRef.current
          );
        });
        return false;
      } finally {
        if (seq === editSeqRef.current) setPendingReplyId(null);
      }
    },
    [
      editPrompt,
      reloadNewest,
      refreshChatList,
      speakIfEnabled,
      stopSpeaking,
      t,
    ],
  );

  // Ask for a new answer to the same prompt (streamed). The old answer stays
  // underneath while it's thinking, so stopping or a failure leaves it intact.
  const regenerate = useCallback(
    async (id: string) => {
      const {
        messages,
        sessionId,
        isTyping: busy,
        temporary,
      } = latestRef.current;
      const at = messages.findIndex((m) => m.id === id);
      const reply = messages[at];
      if (!reply?.serverId || reply.role !== "bot") return;
      if (busy || pendingReplyIdRef.current) {
        toast.error(t("ai-assistant:toasts.wait_for_ai_to_respond"), {
          id: "maya-busy",
        });
        return;
      }
      // Files sent with the prompt go back with the retry, as on the full page.
      const prompt = messages
        .slice(0, at)
        .reverse()
        .find((m) => m.role === "user");
      const attachmentUrls = prompt?.attachments?.map((a) => a.url);

      stopSpeaking();
      setStatus(null);
      setActiveForm(null);
      setPendingReplyId(id);
      const steps: IMayaStep[] = [];
      try {
        await streamRetry(
          space.endpoints.retry,
          reply.serverId,
          {
            onMeta: () => {},
            onStatus: (message) => {
              setStatus(message || null);
              if (message) {
                steps.push({ message, at: formatDate(new Date(), "hh:mm a") });
              }
            },
            onFinal: ({ answer, form, attachments, attachmentErrors }) => {
              setStatus(null);
              setActiveForm(pickFormSpec(answer, form));
              dispatch({
                type: "PATCH_MESSAGE",
                id,
                patch: {
                  text: answer,
                  ...filesPatch(attachments, attachmentErrors),
                  steps: steps.length ? [...steps] : undefined,
                },
              });
              if (answer) speakIfEnabled(answer);
            },
            onMetaFinal: ({ lastAiMessageId }) => {
              if (isStoredId(lastAiMessageId)) {
                dispatch({
                  type: "PATCH_MESSAGE",
                  id,
                  patch: { serverId: lastAiMessageId },
                });
              }
            },
            onDone: () => {
              setStatus(null);
              refreshChatList();
            },
            onError: (err) => {
              setStatus(null);
              toast.error(
                err?.message ??
                  t("common:chrome.maya_couldnt_respond_try_again"),
                { id: "maya-error" },
              );
            },
          },
          attachmentUrls,
          temporary,
          sessionId ?? undefined,
        );
      } finally {
        setPendingReplyId(null);
      }
    },
    [
      streamRetry,
      refreshChatList,
      speakIfEnabled,
      stopSpeaking,
      formatDate,
      t,
      space,
    ],
  );

  /** Start over — a normal chat, or a temporary one. */
  const startChat = useCallback(
    (temporary: boolean) => {
      cancelLoads();
      editSeqRef.current += 1;
      setPendingReplyId(null);
      setActiveForm(null);
      // A reply still streaming belongs to the chat being left.
      if (isTyping) {
        cancel();
        setStatus(null);
      }
      stopSpeaking();
      sessionRemove(space.storage.chat);
      if (userId) sessionSet(STORAGE_KEYS.scopedUser, userId);
      lastUserTextRef.current = null;
      lastUserIdRef.current = null;
      lastUserAttachmentsRef.current = [];
      dispatch({ type: "RESET", temporary });
    },
    [userId, stopSpeaking, isTyping, cancel, cancelLoads, space],
  );
  const resetChat = useCallback(() => startChat(false), [startChat]);
  /** Temporary chat on/off — either way it starts a fresh chat, as on the
   *  full page (a chat can't change from saved to temporary midway). */
  const toggleTemporary = useCallback(
    () => startChat(!state.temporary),
    [startChat, state.temporary],
  );

  // A turn picked from the chat list's prompts: scrolled to and pulsed once
  // the chat has loaded (the message list clears it after the pulse).
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const clearHighlight = useCallback(() => setHighlightId(null), []);

  const loadConversation = useCallback(
    async (sessionId: string, messageId?: number) => {
      const latest = latestRef.current;
      // Switching mid-reply would land that reply in the other chat.
      if (latest.isTyping) return;
      if (sessionId === latest.sessionId) {
        // Back to the chat on screen while another was opening: stay here.
        if (loadingConversationRef.current) cancelLoads();
        if (messageId === undefined) return;
        // A prompt in the chat already open: jump to it if it's loaded. Its
        // id is a local one when it was sent in this session.
        const loaded = latest.messages.find((m) => m.serverId === messageId);
        if (loaded) {
          setHighlightId(loaded.id);
          return;
        }
      }
      const seq = ++loadSeqRef.current;
      stopSpeaking();
      setLoadingConversation(true);
      try {
        // With a prompt to land on, the API returns the page holding it;
        // the newer pages after it are fetched too so nothing in between is
        // missing (page 1 is the newest).
        const first = await getConversation({
          sessionId,
          ...(messageId !== undefined ? { messageId } : { page: 1 }),
          limit: HISTORY_PAGE_SIZE,
        }).unwrap();
        let messages = historyToMessages(first.data.history);
        for (let page = first.meta.page - 1; page >= 1; page--) {
          if (seq !== loadSeqRef.current) return;
          const newer = await getConversation({
            sessionId,
            page,
            limit: HISTORY_PAGE_SIZE,
          }).unwrap();
          const seen = new Set(messages.map((m) => m.id));
          messages = [
            ...messages,
            ...historyToMessages(newer.data.history).filter(
              (m) => !seen.has(m.id),
            ),
          ];
        }
        if (seq !== loadSeqRef.current) return;
        lastUserTextRef.current = null;
        lastUserIdRef.current = null;
        // An edit still waiting for its answer, and a form, belong to the
        // chat being left; the opened chat brings back its own form.
        editSeqRef.current += 1;
        setPendingReplyId(null);
        setActiveForm(formOfLastReply(messages));
        dispatch({
          type: "LOAD_CONVERSATION",
          sessionId,
          messages,
          page: first.meta.page,
          hasEarlier: first.meta.page < first.meta.lastPage,
        });
        const target =
          messageId !== undefined
            ? messages.find((m) => m.serverId === messageId)
            : undefined;
        if (target) setHighlightId(target.id);
      } catch {
        if (seq === loadSeqRef.current) {
          toast.error(t("common:maya.couldnt_load_chat"), { id: "maya-error" });
        }
      } finally {
        if (seq === loadSeqRef.current) setLoadingConversation(false);
      }
    },
    [cancelLoads, getConversation, stopSpeaking, t],
  );

  // The saved chat on screen may have moved on elsewhere — on the full AI
  // Assistant page, or a reply that finished while it was put away — so it
  // is re-read once the sidebar shows it. Waits for a saved chat to be on
  // screen (a restored one arrives a render after mount).
  const savedSessionId = state.temporary ? null : state.sessionId;
  useEffect(() => {
    if (!open || !savedSessionId) return;
    if (!staleChatRef.current && !refreshOnOpenRef.current) return;
    staleChatRef.current = false;
    refreshOnOpenRef.current = false;
    const latest = latestRef.current;
    if (
      latest.isTyping ||
      pendingReplyIdRef.current ||
      loadingConversationRef.current
    )
      return;
    const sessionId = savedSessionId;
    const seq = loadSeqRef.current;
    const edits = editSeqRef.current;
    const snapshot = latest.messages;
    void reloadNewest(sessionId, () => {
      const now = latestRef.current;
      return (
        seq === loadSeqRef.current &&
        edits === editSeqRef.current &&
        now.sessionId === sessionId &&
        now.messages === snapshot &&
        !now.isTyping &&
        !pendingReplyIdRef.current
      );
    }).then((messages) => {
      if (messages) setActiveForm(formOfLastReply(messages));
    });
  }, [open, savedSessionId, staleChatRef, reloadNewest]);

  // Messages without a stored id — restored from a chat saved in the browser
  // before ids were tracked, or whose save event never arrived — can't be
  // edited or regenerated. Re-read the open chat from the server once to pick
  // the ids up: quietly (no skeleton), never mid-reply, once per chat.
  const syncedSessionRef = useRef<string | null>(null);
  // Bumped to try again when a sync was dropped because the chat changed.
  const [syncAttempt, setSyncAttempt] = useState(0);
  useEffect(() => {
    const sessionId = state.sessionId;
    if (
      !sessionId ||
      state.temporary ||
      isTyping ||
      loadingConversation ||
      pendingReplyId
    )
      return;
    if (syncedSessionRef.current === sessionId) return;
    const unsaved = state.messages.some(
      (m) => !m.i18nKey && !m.serverId && m.status !== "failed",
    );
    if (!unsaved) return;
    syncedSessionRef.current = sessionId;
    const seq = loadSeqRef.current;
    const snapshot = state.messages;
    void reloadNewest(sessionId, () => {
      const latest = latestRef.current;
      if (seq !== loadSeqRef.current || latest.sessionId !== sessionId)
        return false;
      // Anything on screen changed meanwhile (a turn sent, an edit): don't
      // overwrite it — try again once things are quiet.
      if (
        latest.messages !== snapshot ||
        latest.isTyping ||
        pendingReplyIdRef.current
      ) {
        syncedSessionRef.current = null;
        setSyncAttempt((n) => n + 1);
        return false;
      }
      return true;
    });
  }, [
    state.sessionId,
    state.temporary,
    state.messages,
    isTyping,
    loadingConversation,
    pendingReplyId,
    reloadNewest,
    syncAttempt,
  ]);

  const { sessionId: openSessionId, cursor } = state;
  const loadEarlier = useCallback(async () => {
    const sessionId = openSessionId;
    if (!sessionId || !cursor.hasEarlier || loadingEarlier) return;
    const seq = loadSeqRef.current;
    const page = cursor.page + 1;
    setLoadingEarlier(true);
    try {
      const res = await getConversation({
        sessionId,
        page,
        limit: HISTORY_PAGE_SIZE,
      }).unwrap();
      if (seq !== loadSeqRef.current) return;
      dispatch({
        type: "PREPEND_HISTORY",
        messages: historyToMessages(res.data.history),
        cursor: { page, hasEarlier: res.meta.page < res.meta.lastPage },
      });
    } catch {
      if (seq === loadSeqRef.current) {
        toast.error(t("common:maya.couldnt_load_chat"), { id: "maya-error" });
      }
    } finally {
      setLoadingEarlier(false);
    }
  }, [openSessionId, cursor, loadingEarlier, getConversation, t]);

  const value = useMemo<ChatContextValue>(
    () => ({
      messages: state.messages,
      isTyping,
      error: state.error,
      sessionId: state.sessionId,
      hasEarlier: state.cursor.hasEarlier,
      temporary: state.temporary,
      toggleTemporary,
      highlightId,
      clearHighlight,
      advisor,
      selectAdvisor,
      pendingReplyId,
      activeForm,
      closeForm,
      loadingConversation,
      loadingEarlier,
      status,
      readAloud,
      pageContext,
      sendMessage,
      retryLast,
      resetChat,
      loadConversation,
      loadEarlier,
      editMessage,
      regenerate,
      toggleReadAloud,
      stop,
    }),
    [
      state.messages,
      state.error,
      state.sessionId,
      state.cursor.hasEarlier,
      state.temporary,
      toggleTemporary,
      highlightId,
      clearHighlight,
      advisor,
      selectAdvisor,
      pendingReplyId,
      activeForm,
      closeForm,
      loadingConversation,
      loadingEarlier,
      isTyping,
      status,
      readAloud,
      pageContext,
      sendMessage,
      retryLast,
      resetChat,
      loadConversation,
      loadEarlier,
      editMessage,
      regenerate,
      toggleReadAloud,
      stop,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
